import { useEffect, useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { Button } from "../base/Button";
import usePinnacleMappingMutations from "../../data/PinnacleMapping/usePinnacleMappingMutations";
import {
  IPinnacleItemType,
  IPinnacleRule,
  ISavePinnacleRule,
  PinnaclePosition,
  PinnacleSide,
} from "../../models/PinnacleMapping/PinnacleMapping";
import { CategoryOption, CategoryPicker } from "./CategoryPicker";
import { prettyItemTypeName, sideLabel } from "./pinnacleMeta";
import { useNewCategory } from "./useNewCategory";

const gridCols = "sm:grid-cols-[8rem_8rem_minmax(6rem,1fr)_minmax(12rem,2fr)_auto]";

/**
 * The mappings confirmed for one item type. Each row is saved on its own; a new row is a draft until
 * it is saved, so nothing reaches customers half-filled.
 */
export function MappingEditor({ itemType, categories }: { itemType: IPinnacleItemType; categories: CategoryOption[] }) {
  const { confirmRule, setIgnored } = usePinnacleMappingMutations();
  const [drafts, setDrafts] = useState<number[]>([]);

  // A fresh item type starts with one empty row to fill in, rather than an empty list and a button.
  useEffect(() => {
    setDrafts(itemType.rules.length === 0 ? [Date.now()] : []);
  }, [itemType.id]);

  const unchecked = itemType.rules.filter((r) => !r.confirmedAt);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pinnacle item type</p>
          <h2 className="mt-1 flex flex-wrap items-center gap-2">
            <span className="rounded bg-yellow-400 px-2 py-0.5 font-mono text-base font-medium text-yellow-950">{itemType.name}</span>
            <span className="text-xs text-gray-500">
              key <code>{itemType.key}</code>
              {itemType.itemTypeId != null && <> · itemtype_id {itemType.itemTypeId}</>}
            </span>
          </h2>
        </div>
        <Button
          variant="outlined"
          size="sm"
          onClick={() => setIgnored.mutate({ itemTypeId: itemType.id, ignored: !itemType.ignored })}
          disabled={setIgnored.isPending}
        >
          {itemType.ignored ? "Stop ignoring" : "Ignore item type"}
        </Button>
      </div>

      {unchecked.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unchecked.length === 1 ? "One mapping was" : `${unchecked.length} mappings were`} copied from the old Pinnacle name
          on the category and nobody has checked {unchecked.length === 1 ? "it" : "them"} yet. Parts are filed this way until
          someone changes or confirms it.
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mappings</p>
        <div className={clsx("mt-2 hidden gap-2 px-2 text-xs font-medium text-gray-500 sm:grid", gridCols)}>
          <span>Side</span>
          <span>Position</span>
          <span>Keyword</span>
          <span>Maps to category</span>
          <span />
        </div>
        <div className="mt-1 space-y-2">
          {itemType.rules.map((rule) => (
            <MappingRow
              key={rule.id}
              itemTypeId={itemType.id}
              itemTypeName={itemType.name}
              rule={rule}
              categories={categories}
              onConfirm={() => confirmRule.mutate(rule.id)}
            />
          ))}
          {drafts.map((draft) => (
            <MappingRow
              key={draft}
              itemTypeId={itemType.id}
              itemTypeName={itemType.name}
              categories={categories}
              onDone={() => setDrafts((d) => d.filter((x) => x !== draft))}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setDrafts((d) => [...d, Date.now()])}
          className="mt-2 rounded px-2 py-1 text-sm font-medium text-orange-700 hover:bg-orange-50"
        >
          + Add mapping
        </button>
        <p className="mt-1 text-xs text-gray-500">
          A mapping with no side, position or keyword is the default. The most specific mapping that fits a part wins.
        </p>
      </div>
    </div>
  );
}

function MappingRow({
  itemTypeId,
  itemTypeName,
  rule,
  categories,
  onConfirm,
  onDone,
}: {
  itemTypeId: string;
  itemTypeName: string;
  rule?: IPinnacleRule;
  categories: CategoryOption[];
  onConfirm?: () => void;
  onDone?: () => void;
}) {
  const { createRule, updateRule, deleteRule } = usePinnacleMappingMutations();
  const newCategory = useNewCategory();

  const initial = (): ISavePinnacleRule => ({
    partCategoryId: rule?.partCategoryId ?? "",
    side: rule?.side ?? null,
    position: rule?.position ?? null,
    keyword: rule?.keyword ?? null,
    priority: rule?.priority ?? 0,
  });

  const [draft, setDraft] = useState<ISavePinnacleRule>(initial);

  // Picks up the saved values after a save or another tab's change, without losing an unsaved edit
  // to a different row.
  useEffect(() => setDraft(initial()), [rule?.partCategoryId, rule?.side, rule?.position, rule?.keyword, rule?.priority]);

  const dirty =
    !rule ||
    draft.partCategoryId !== rule.partCategoryId ||
    draft.side !== rule.side ||
    draft.position !== rule.position ||
    (draft.keyword ?? "") !== (rule.keyword ?? "");

  const saving = createRule.isPending || updateRule.isPending;
  const id = rule?.id ?? `new-${itemTypeId}`;

  const save = () => {
    const body = { ...draft, keyword: draft.keyword?.trim() || null };

    if (rule) updateRule.mutate({ ruleId: rule.id, rule: body });
    else createRule.mutate({ itemTypeId, rule: body }, { onSuccess: () => onDone?.() });
  };

  return (
    <div className={clsx("rounded-md border bg-gray-50 p-2", rule ? "border-gray-200" : "border-dashed border-orange-300")}>
      <div className={clsx("grid grid-cols-2 items-center gap-2", gridCols)}>
        <select
          id={`side-${id}`}
          aria-label="Side"
          value={draft.side ?? ""}
          onChange={(e) => setDraft({ ...draft, side: (e.target.value || null) as PinnacleSide | null })}
          className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">Any side</option>
          <option value="Left">{sideLabel.Left}</option>
          <option value="Right">{sideLabel.Right}</option>
        </select>
        <select
          id={`position-${id}`}
          aria-label="Position"
          value={draft.position ?? ""}
          onChange={(e) => setDraft({ ...draft, position: (e.target.value || null) as PinnaclePosition | null })}
          className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">Any position</option>
          <option value="Front">Front</option>
          <option value="Rear">Rear</option>
        </select>
        <input
          id={`keyword-${id}`}
          aria-label="Keyword"
          value={draft.keyword ?? ""}
          onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
          placeholder="Keyword"
          className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
        />
        <CategoryPicker
          className="col-span-2 sm:col-span-1"
          categories={newCategory.withCreated(categories)}
          value={draft.partCategoryId || null}
          onChange={(categoryId) => setDraft({ ...draft, partCategoryId: categoryId ?? "" })}
          onCreate={(typed) =>
            newCategory.start(typed || prettyItemTypeName(itemTypeName), (created) =>
              setDraft((d) => ({ ...d, partCategoryId: created.id })),
            )
          }
        />
        {newCategory.modal}
        <div className="col-span-2 flex justify-end gap-1 sm:col-span-1">
          {dirty && (
            <Button size="sm" onClick={save} disabled={saving || !draft.partCategoryId}>
              {rule ? "Save" : "Add"}
            </Button>
          )}
          <button
            type="button"
            aria-label={rule ? "Remove mapping" : "Discard new mapping"}
            onClick={() => (rule ? deleteRule.mutate(rule.id) : onDone?.())}
            disabled={deleteRule.isPending}
            className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <TrashIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      {rule && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 px-1 text-xs text-gray-500">
          {rule.confirmedAt ? (
            <span>
              <span className="rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700">
                {rule.origin === "Suggestion" ? "From suggestion" : rule.origin === "Legacy" ? "Legacy, checked" : "Confirmed"}
              </span>{" "}
              by {rule.confirmedBy ?? "unknown"} on {new Date(rule.confirmedAt).toLocaleDateString()}
            </span>
          ) : (
            <>
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">Legacy · not checked</span>
              <button type="button" onClick={onConfirm} className="font-medium text-orange-700 hover:underline">
                Mark as checked
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
